import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { usersApi, type UsersListQuery } from "../api/users";
import type {
  AccountChange,
  AdminRoleName,
  AdministratorListPage,
  CreatedAdministrator,
  PasswordReset,
} from "../api/types";

/**
 * Reading administrators, and changing what they may do.
 *
 * Every key starts with "users", so one invalidation after any change makes
 * every view of the list agree again. That matters more here than elsewhere:
 * a role change signs the target out, and a table still showing "2 sessions"
 * next to somebody who was just demoted is a screen an owner would act on.
 *
 * NOTHING IS OPTIMISTIC. No role is written into the cache when a button is
 * pressed, only when the server has said what the account now holds. Authority
 * shown before it exists is authority somebody will rely on, and every one of
 * these mutations can be refused — by the last-owner invariant, by the
 * self-action rule, or by a mistyped password.
 */
export const usersKeys = {
  all: ["users"] as const,
  list: (query: UsersListQuery) => ["users", "list", query] as const,
};

export const USERS_PAGE_SIZE = 25;

export function useUsersList(query: UsersListQuery) {
  return useQuery<AdministratorListPage>({
    queryKey: usersKeys.list(query),
    queryFn: ({ signal }) => usersApi.list(query, signal),
    // The previous page stays on screen while the next one loads, so paging
    // and typing do not blank the table between answers.
    placeholderData: (previous) => previous,
  });
}

export function useCreateAdministrator() {
  const queryClient = useQueryClient();
  return useMutation<
    CreatedAdministrator,
    unknown,
    { email: string; display_name: string; current_password: string }
  >({
    mutationFn: (body) => usersApi.create(body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useRoleChange() {
  const queryClient = useQueryClient();
  return useMutation<
    AccountChange,
    unknown,
    {
      adminId: string;
      role: AdminRoleName;
      grant: boolean;
      currentPassword: string;
    }
  >({
    mutationFn: ({ adminId, role, grant, currentPassword }) =>
      grant
        ? usersApi.grantRole(adminId, role, currentPassword)
        : usersApi.revokeRole(adminId, role, currentPassword),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useAccountAction() {
  const queryClient = useQueryClient();
  return useMutation<
    AccountChange,
    unknown,
    { adminId: string; action: "disable" | "reactivate"; currentPassword: string }
  >({
    mutationFn: ({ adminId, action, currentPassword }) =>
      action === "disable"
        ? usersApi.disable(adminId, currentPassword)
        : usersApi.reactivate(adminId, currentPassword),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation<
    PasswordReset,
    unknown,
    { adminId: string; currentPassword: string }
  >({
    mutationFn: ({ adminId, currentPassword }) =>
      usersApi.resetPassword(adminId, currentPassword),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
}
